import { Drawer, Box, Typography, Stack } from '@mui/material';
import { MenuContent } from './MenuContent';
import { UserProfile } from './UserProfile';

const DRAWER_WIDTH = 240;

export interface MobileDrawerProps {
  /**
   * Whether the drawer is open
   */
  open: boolean;

  /**
   * Callback when drawer should close
   */
  onClose: () => void;

  /**
   * User's display name
   */
  name: string;

  /**
   * User's email address
   */
  email: string;

  /**
   * Optional avatar URL
   */
  avatarUrl?: string;

  /**
   * Callback when logout is clicked
   */
  onLogout: () => void;
}

/**
 * Mobile Drawer Component
 *
 * Temporary drawer for mobile navigation.
 * Slides in from the left and overlays the content.
 *
 * Visible only on xs-sm breakpoints (<900px).
 *
 * @example
 * ```tsx
 * <MobileDrawer
 *   open={drawerOpen}
 *   onClose={() => setDrawerOpen(false)}
 *   name="John Doe"
 *   email="john@example.com"
 *   onLogout={handleLogout}
 * />
 * ```
 */
export function MobileDrawer({
  open,
  onClose,
  name,
  email,
  avatarUrl,
  onLogout,
}: MobileDrawerProps) {
  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better mobile performance
      }}
      sx={{
        display: { xs: 'block', md: 'none' },
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Logo / Brand Section */}
        <Box
          sx={{
            display: 'flex',
            p: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 1 }}>
            {/* Logo placeholder */}
            <Box
              component="img"
              src="/assets/logo.png"
              alt="StyleConnect"
              sx={{
                width: 24,
                height: 24,
                borderRadius: 0.5,
                objectFit: 'contain',
              }}
            />
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              StyleConnect
            </Typography>
          </Stack>
        </Box>

        {/* Navigation Menu */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <MenuContent onItemClick={onClose} onLogout={onLogout} />
        </Box>

        {/* User Profile */}
        <UserProfile name={name} email={email} avatarUrl={avatarUrl} onLogout={onLogout} />
      </Box>
    </Drawer>
  );
}
